import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, CheckCircle, Clock, RefreshCw, Mail, MessageCircle, Send, Eye, EyeOff, Shield, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { getSettings, updateSettings, getPublicSettings, updateTtdStempel } from '../api';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function Settings() {
  const [, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Log retention state
  const [logRetentionDays, setLogRetentionDays] = useState('30');

  // SMTP state
  const [smtp, setSmtp] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'LayananSynexa',
    notif_email_enabled: 'false',
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Telegram state
  const [telegram, setTelegram] = useState({
    telegram_bot_token: '',
    notif_telegram_enabled: 'false',
  });
  const [showBotToken, setShowBotToken] = useState(false);

  // CRM delete password state
  const [crmDeletePassword, setCrmDeletePassword] = useState('');
  const [showCrmPassword, setShowCrmPassword] = useState(false);

  // TTD & Stempel default state (untuk cetak dokumen CM)
  const [ttdDirektur, setTtdDirektur] = useState('');
  const [stempelPerusahaan, setStempelPerusahaan] = useState('');
  const [uploadingImage, setUploadingImage] = useState(''); // 'ttd' | 'stempel' | ''
  // Nilai yang tersimpan di server (referensi untuk indikator "belum disimpan")
  const [savedTtd, setSavedTtd] = useState('');
  const [savedStempel, setSavedStempel] = useState('');
  const ttdStempelDirty = ttdDirektur !== savedTtd || stempelPerusahaan !== savedStempel;

  const load = () => {
    setLoading(true);
    setError('');
    // TTD & stempel dapat dibaca oleh semua user yang login (bukan admin-only)
    getPublicSettings()
      .then(res => {
        setTtdDirektur(res.data.ttd_direktur || '');
        setStempelPerusahaan(res.data.stempel_perusahaan || '');
        setSavedTtd(res.data.ttd_direktur || '');
        setSavedStempel(res.data.stempel_perusahaan || '');
      })
      .catch(() => {});
    getSettings()
      .then(res => {
        setSettings(res.data);
        setLogRetentionDays(res.data.log_retention_days || '30');
        setSmtp({
          smtp_host: res.data.smtp_host || '',
          smtp_port: res.data.smtp_port || '587',
          smtp_username: res.data.smtp_username || '',
          smtp_password: res.data.smtp_password || '',
          smtp_from_email: res.data.smtp_from_email || '',
          smtp_from_name: res.data.smtp_from_name || 'LayananSynexa',
          notif_email_enabled: res.data.notif_email_enabled || 'false',
        });
        setTelegram({
          telegram_bot_token: res.data.telegram_bot_token || '',
          notif_telegram_enabled: res.data.notif_telegram_enabled || 'false',
        });
        setCrmDeletePassword(res.data.crm_delete_password || '');
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Gagal memuat pengaturan');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleSaveLogRetention = async () => {
    const days = parseInt(logRetentionDays, 10);
    if (isNaN(days) || days < 1 || days > 365) {
      setError('Nilai harus antara 1 - 365 hari');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateSettings({ log_retention_days: String(days) });
      setSettings(res.data);
      showSuccess(`Pengaturan berhasil disimpan. Log akan otomatis dihapus setelah ${days} hari.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateSettings({
        smtp_host: smtp.smtp_host,
        smtp_port: smtp.smtp_port,
        smtp_username: smtp.smtp_username,
        smtp_password: smtp.smtp_password,
        smtp_from_email: smtp.smtp_from_email,
        smtp_from_name: smtp.smtp_from_name,
        notif_email_enabled: smtp.notif_email_enabled,
      });
      setSettings(res.data);
      showSuccess('Pengaturan email berhasil disimpan.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan pengaturan email');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCrmPassword = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateSettings({ crm_delete_password: crmDeletePassword });
      showSuccess('Password hapus CRM berhasil disimpan.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan password CRM');
    } finally {
      setSaving(false);
    }
  };

  // Konversi file gambar ke base64 data URL (PNG/JPG)
  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Gagal membaca file'));
      reader.readAsDataURL(file);
    });
  };

  const handleUploadImage = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('File harus berupa gambar (PNG/JPG).');
      return;
    }
    setUploadingImage(type);
    setError('');
    try {
      const dataUrl = await fileToDataUrl(file);
      if (type === 'ttd') setTtdDirektur(dataUrl);
      else setStempelPerusahaan(dataUrl);
    } catch (err) {
      setError(err.message || 'Gagal mengupload gambar');
    } finally {
      setUploadingImage('');
    }
  };

  const handleSaveTtdStempel = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateTtdStempel({
        ttd_direktur: ttdDirektur,
        stempel_perusahaan: stempelPerusahaan,
      });
      setSavedTtd(ttdDirektur);
      setSavedStempel(stempelPerusahaan);
      showSuccess('TTD digital & stempel perusahaan berhasil disimpan. Akan otomatis muncul di cetakan CM.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan TTD & stempel');
    } finally {
      setSaving(false);
    }
  };

  const ImageUploadBox = ({ value, onUpload, onClear, label, desc, placeholder }) => (
    <div style={{ flex: 1, minWidth: 220, border: '1px dashed #d1d5db', borderRadius: 8, padding: 16, textAlign: 'center', background: '#fafafa' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginBottom: 10 }}>{desc}</div>
      {value ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <img src={value} alt={label} style={{ maxHeight: 90, maxWidth: '100%', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: 4 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4 }}>
              <Upload size={12} /> Ganti
              <input type="file" hidden accept="image/png,image/jpeg" onChange={onUpload} />
            </label>
            <button type="button" onClick={onClear}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
              <Trash2 size={12} /> Hapus
            </button>
          </div>
        </div>
      ) : (
        <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6 }}>
          <Upload size={14} /> {placeholder}
          <input type="file" hidden accept="image/png,image/jpeg" onChange={onUpload} />
        </label>
      )}
    </div>
  );

  const handleSaveTelegram = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await updateSettings({
        telegram_bot_token: telegram.telegram_bot_token,
        notif_telegram_enabled: telegram.notif_telegram_enabled,
      });
      setSettings(res.data);
      showSuccess('Pengaturan Telegram berhasil disimpan.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan pengaturan Telegram');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/settings/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess('✅ Email uji coba terkirim! Cek inbox Anda.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal kirim email uji coba');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestTelegram = async () => {
    setTestingTelegram(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/settings/test-telegram`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess('✅ Pesan Telegram uji coba terkirim! Cek aplikasi Telegram Anda.');
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal kirim Telegram uji coba');
    } finally {
      setTestingTelegram(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h2>Pengaturan Sistem</h2>
        </div>
        <div className="page-loading">Memuat pengaturan...</div>
      </div>
    );
  }

  const Card = ({ children, style }) => (
    <div style={{
      background: 'white',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow)',
      border: '1px solid var(--gray-200)',
      overflow: 'hidden',
      marginBottom: 24,
      ...style,
    }}>
      {children}
    </div>
  );

  const CardHeader = ({ icon, color, title, desc }) => (
    <div style={{
      padding: '20px 24px',
      borderBottom: '1px solid var(--gray-100)',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: `var(--${color}-tint-bg)` || '#eef2ff',
        color: `var(--${color})` || '#6366f1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--gray-600)' }}>{title}</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: 2 }}>{desc}</p>
      </div>
    </div>
  );

  const FormGroup = ({ label, children }) => (
    <div className="form-group" style={{ minWidth: 200 }}>
      <label>{label}</label>
      {children}
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Pengaturan Sistem</h2>
            <p className="page-desc">Konfigurasi sistem LayananSynexa</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 20 }}>
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', background: '#d1fae5',
          border: '1px solid #a7f3d0', borderRadius: 'var(--radius)',
          color: '#065f46', fontWeight: 500, fontSize: '0.85rem',
          marginBottom: 20
        }}>
          <CheckCircle size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* === LOG RETENTION === */}
      <Card>
        <CardHeader icon={<Clock size={20} />} color="info" title="Retensi Log Sistem"
          desc="Atur berapa lama log sistem disimpan sebelum otomatis dihapus" />
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <FormGroup label="Hapus Log Setelah (hari)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min={1} max={365} value={logRetentionDays}
                  onChange={e => setLogRetentionDays(e.target.value)}
                  style={{ padding: '10px 14px', border: '2px solid var(--gray-200)',
                    borderRadius: 'var(--radius)', fontSize: '1rem', fontWeight: 600,
                    color: 'var(--gray-700)', outline: 'none', width: 120 }}
                  onFocus={e => e.target.style.borderColor = 'var(--info)'}
                  onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
                />
                <span style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>hari</span>
              </div>
            </FormGroup>
            <button className="btn btn-primary" onClick={handleSaveLogRetention} disabled={saving}>
              {saving ? <><RefreshCw size={16} className="spinner" /> Menyimpan...</> : <><Save size={16} /> Simpan</>}
            </button>
          </div>
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--gray-50)',
            borderRadius: 'var(--radius)', fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--gray-600)' }}>Info:</strong>{' '}
            Log cleanup scheduler berjalan setiap 1 jam. Nilai saat ini: <strong style={{ color: 'var(--info)' }}>{logRetentionDays} hari</strong>.
          </div>
        </div>
      </Card>

      {/* === SMTP EMAIL CONFIG === */}
      <Card>
        <CardHeader icon={<Mail size={20} />} color="primary"
          title="Notifikasi Email (SMTP)"
          desc="Konfigurasi SMTP untuk mengirim notifikasi ke admin via email" />
        <div style={{ padding: '24px' }}>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormGroup label="SMTP Host">
              <input type="text" value={smtp.smtp_host}
                onChange={e => setSmtp({...smtp, smtp_host: e.target.value})}
                placeholder="smtp.gmail.com" />
            </FormGroup>
            <FormGroup label="SMTP Port">
              <input type="text" value={smtp.smtp_port}
                onChange={e => setSmtp({...smtp, smtp_port: e.target.value})}
                placeholder="587" />
            </FormGroup>
            <FormGroup label="SMTP Username">
              <input type="text" value={smtp.smtp_username}
                onChange={e => setSmtp({...smtp, smtp_username: e.target.value})}
                placeholder="user@gmail.com" />
            </FormGroup>
            <FormGroup label="SMTP Password">
              <div className="password-input">
                <input type={showSmtpPassword ? 'text' : 'password'} value={smtp.smtp_password}
                  onChange={e => setSmtp({...smtp, smtp_password: e.target.value})}
                  placeholder="App password" />
                <button type="button" className="password-toggle"
                  onClick={() => setShowSmtpPassword(!showSmtpPassword)} tabIndex={-1}>
                  {showSmtpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </FormGroup>
            <FormGroup label="From Email">
              <input type="email" value={smtp.smtp_from_email}
                onChange={e => setSmtp({...smtp, smtp_from_email: e.target.value})}
                placeholder="noreply@domain.com" />
            </FormGroup>
            <FormGroup label="From Name">
              <input type="text" value={smtp.smtp_from_name}
                onChange={e => setSmtp({...smtp, smtp_from_name: e.target.value})}
                placeholder="LayananSynexa" />
            </FormGroup>
            <FormGroup label="Aktifkan Notifikasi Email">
              <select value={smtp.notif_email_enabled}
                onChange={e => setSmtp({...smtp, notif_email_enabled: e.target.value})}>
                <option value="true">✅ Aktif</option>
                <option value="false">❌ Nonaktif</option>
              </select>
            </FormGroup>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveSmtp} disabled={saving}>
              {saving ? <><RefreshCw size={16} className="spinner" /> Menyimpan...</> : <><Save size={16} /> Simpan Email</>}
            </button>
            <button className="btn btn-secondary" onClick={handleTestEmail} disabled={testingEmail}>
              {testingEmail ? <><RefreshCw size={16} className="spinner" /> Mengirim...</> : <><Send size={16} /> Uji Coba Email</>}
            </button>
          </div>
        </div>
      </Card>

      {/* === TELEGRAM CONFIG === */}
      <Card>
        <CardHeader icon={<MessageCircle size={20} />} color="success"
          title="Notifikasi Telegram Bot"
          desc="Konfigurasi bot Telegram untuk mengirim notifikasi ke admin" />
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef3c7',
            borderRadius: 'var(--radius)', fontSize: '0.8rem', color: '#92400e', lineHeight: 1.6 }}>
            <strong>📋 Cara Setup:</strong>
            <ol style={{ margin: '8px 0 0 16px', padding: 0 }}>
              <li>Buka Telegram, cari <strong>@BotFather</strong></li>
              <li>Kirim <code>/newbot</code> dan ikuti petunjuk untuk membuat bot baru</li>
              <li>Copy <strong>token</strong> yang diberikan (format: <code>123456:ABC-DEF1234</code>)</li>
              <li>Paste token di bawah, simpan, lalu klik <strong>Uji Coba</strong></li>
              <li>Setiap admin harus set <strong>Telegram Chat ID</strong> di menu <strong>Management User</strong></li>
            </ol>
          </div>

          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FormGroup label="Bot Token">
              <div className="password-input">
                <input type={showBotToken ? 'text' : 'password'} value={telegram.telegram_bot_token}
                  onChange={e => setTelegram({...telegram, telegram_bot_token: e.target.value})}
                  placeholder="123456:ABC-DEF1234" />
                <button type="button" className="password-toggle"
                  onClick={() => setShowBotToken(!showBotToken)} tabIndex={-1}>
                  {showBotToken ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </FormGroup>
            <FormGroup label="Aktifkan Notifikasi Telegram">
              <select value={telegram.notif_telegram_enabled}
                onChange={e => setTelegram({...telegram, notif_telegram_enabled: e.target.value})}>
                <option value="true">✅ Aktif</option>
                <option value="false">❌ Nonaktif</option>
              </select>
            </FormGroup>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSaveTelegram} disabled={saving}>
              {saving ? <><RefreshCw size={16} className="spinner" /> Menyimpan...</> : <><Save size={16} /> Simpan Telegram</>}
            </button>
            <button className="btn btn-secondary" onClick={handleTestTelegram} disabled={testingTelegram}>
              {testingTelegram ? <><RefreshCw size={16} className="spinner" /> Mengirim...</> : <><Send size={16} /> Uji Coba Telegram</>}
            </button>
          </div>
        </div>
      </Card>

      {/* === CRM DELETE PASSWORD === */}
      <Card>
        <CardHeader icon={<Shield size={20} />} color="warning"
          title="Password Hapus CRM"
          desc="Atur password yang diperlukan untuk menghapus data di Pipeline CRM" />
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef3c7',
            borderRadius: 'var(--radius)', fontSize: '0.8rem', color: '#92400e', lineHeight: 1.6 }}>
            <strong>🔒 Keamanan:</strong> Saat menghapus deal di Pipeline CRM, user harus memasukkan password ini beserta keterangan alasan penghapusan.
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
            <FormGroup label="Password Hapus">
              <div className="password-input" style={{ position: 'relative' }}>
                <input type={showCrmPassword ? 'text' : 'password'} value={crmDeletePassword}
                  onChange={e => setCrmDeletePassword(e.target.value)}
                  placeholder="Masukkan password hapus"
                  style={{ width: 250 }} />
                <button type="button" className="password-toggle"
                  onClick={() => setShowCrmPassword(!showCrmPassword)} tabIndex={-1}>
                  {showCrmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </FormGroup>
            <button className="btn btn-primary" onClick={handleSaveCrmPassword} disabled={saving}>
              {saving ? <><RefreshCw size={16} className="spinner" /> Menyimpan...</> : <><Save size={16} /> Simpan</>}
            </button>
          </div>
        </div>
      </Card>

      {/* === TTD & STEMPEL PERUSAHAAN === */}
      <Card>
        <CardHeader icon={<ImageIcon size={20} />} color="primary"
          title="TTD Digital & Stempel Perusahaan"
          desc="Default tanda tangan & stempel untuk cetakan dokumen CM (muncul otomatis di pojok kanan bawah)" />
        <div style={{ padding: '24px' }}>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#eff6ff',
            borderRadius: 'var(--radius)', fontSize: '0.8rem', color: '#1e40af', lineHeight: 1.6 }}>
            <strong>ℹ️ Info:</strong> Upload TTD digital (mis. Achmad Saudi — Direktur Operasional) dan stempel
            perusahaan di sini. Setelah disimpan, keduanya otomatis muncul di bagian <strong>"Diperiksa &amp; Diterima oleh"</strong>
            pojok kanan bawah saat mencetak laporan CM Selesai.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <ImageUploadBox
              value={ttdDirektur}
              onUpload={(e) => handleUploadImage(e, 'ttd')}
              onClear={() => setTtdDirektur('')}
              label="TTD Digital (Direktur)"
              desc="Gambar tanda tangan, background transparan (PNG disarankan)"
              placeholder={uploadingImage === 'ttd' ? 'Memuat...' : 'Upload TTD'}
            />
            <ImageUploadBox
              value={stempelPerusahaan}
              onUpload={(e) => handleUploadImage(e, 'stempel')}
              onClear={() => setStempelPerusahaan('')}
              label="Stempel Perusahaan"
              desc="Gambar stempel bulat, background transparan (PNG disarankan)"
              placeholder={uploadingImage === 'stempel' ? 'Memuat...' : 'Upload Stempel'}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleSaveTtdStempel} disabled={saving}>
              {saving ? <><RefreshCw size={16} className="spinner" /> Menyimpan...</> : <><Save size={16} /> Simpan TTD & Stempel</>}
            </button>
            {ttdStempelDirty && (
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '5px 10px' }}>
                ⚠️ Belum disimpan — klik <strong>Simpan TTD &amp; Stempel</strong> agar tersimpan &amp; muncul di cetakan
              </span>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
